import {
	Add as AddIcon,
	Check as CheckIcon,
	Clear as ClearIcon,
	Close as CloseIcon,
	Delete as DeleteIcon,
	Edit as EditIcon,
	History as HistoryIcon,
	Save as SaveIcon,
} from '@mui/icons-material';
import { Box, Button, Checkbox, CircularProgress, Grid, IconButton, TextField } from '@mui/material';
import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	GridToolbarContainer,
	GridToolbarFilterButton,
} from '@mui/x-data-grid-pro';
import { useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import { GridToolbarSlot, gridToolbarProps } from '@parkchirp/ui-library';
import { ResourceTypes } from 'parkchirp-shared/enums';
import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { CommonContext } from '../../contexts/common.context';
import { useDebounce } from '../../hooks/useDebounce';
import {
	CREATE_HOURLY_ACCOUNT_USER_V2,
	DELETE_HOURLY_ACCOUNT_USER_V2,
	GET_USER_EXISTS_V2,
	LIST_HOURLY_ACCOUNT_USERS_V2,
	UPDATE_HOURLY_ACCOUNT_USER_V2,
} from '../../queries';
import type { HourlyAccount, HourlyAccountUser, HourlyAccountUserGroupValidation } from '../../types/hourly';
import type { PermissionFunctionGroup } from '../../types/permissions';
import { AuditHistoryModal } from '../audit/auditHistoryModal';
import { PermissionFunctionGroupSelect } from '../inputs/permissionFunctionGroupSelect';
import { ValidationMethodSelect } from '../inputs/validationMethodSelect';
import { ConfirmationInputModal } from '../modals/confirmationInputModal';
import { SimpleModal } from '../modals/simpleModal';

const defaultSortOrder = {
	sort: 'desc',
	field: 'lastName',
};

interface PortalUsersToolbarProps {
	editing: boolean;
	onCreateAccount: () => void;
}

const PortalUsersToolbar = ({ editing, onCreateAccount }: PortalUsersToolbarProps) => (
	<GridToolbarContainer>
		<Grid sx={{ display: 'flex', flex: 1 }}>
			<GridToolbarFilterButton data-cy='filter-table-button' />
		</Grid>
		<Grid sx={{ display: 'flex', justifyContent: 'end' }}>
			<Grid item>
				<Button size='small' onClick={onCreateAccount} disabled={editing}>
					<AddIcon /> Add User
				</Button>
			</Grid>
		</Grid>
	</GridToolbarContainer>
);

interface EditorState {
	isNew: boolean;
	isEditing: boolean;
}

interface PortalUsersEditorProps {
	disabled: boolean;
	hourlyAccount?: HourlyAccount;
	features: PermissionFunctionGroup['functionGroup'][];
	validations: HourlyAccountUserGroupValidation[];
	refetchToken: number | null;
}

export const PortalUsersEditor = ({
	disabled,
	hourlyAccount,
	features,
	validations,
	refetchToken,
}: PortalUsersEditorProps) => {
	const { setShowSnackbar, setShowDialog, apolloHeaders } = useContext(CommonContext);

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [rowCount, setRowCount] = useState<number>(0);
	const [rows, setRows] = useState([]);
	const [sortOrder, setSortOrder] = useState(defaultSortOrder);
	const [filters, setFilters] = useState([]);
	const debouncedSetFilters = useDebounce(setFilters, 500);

	const [editingModel, setEditingModel] = useState<(HourlyAccountUser & EditorState) | null>(null);

	const { data, loading, refetch } = useQuery(LIST_HOURLY_ACCOUNT_USERS_V2, {
		variables: {
			limit: pageSize,
			offset: page * pageSize,
			filters,
			order: sortOrder,
			hourlyAccountId: hourlyAccount?.id,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
		skip: !hourlyAccount?.id,
	});

	const [getUserExists] = useLazyQuery(GET_USER_EXISTS_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const [createHourlyAccountUser, { loading: createUserLoading }] = useMutation(CREATE_HOURLY_ACCOUNT_USER_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const [updateHourlyAccountUser, { loading: updateUserLoading }] = useMutation(UPDATE_HOURLY_ACCOUNT_USER_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const [deleteHourlyAccountUser] = useMutation(DELETE_HOURLY_ACCOUNT_USER_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	useEffect(() => {
		if (refetchToken) {
			refetch();
		}
	}, [refetchToken, refetch]);

	useEffect(() => {
		if (!loading && data) {
			setRows(data?.listHourlyAccountUsers?.hourlyAccountUsers);
			setRowCount(data?.listHourlyAccountUsers?.count);
		}
	}, [loading, data]);

	const onFilterModelChange = useCallback(
		(filterModel: GridFilterModel) => {
			debouncedSetFilters(
				filterModel.items?.map((filterItem) => ({
					propertyName: filterItem.field,
					op: filterItem.operator,
					value: filterItem.value,
				})),
			);
		},
		[debouncedSetFilters],
	);

	const onSortModelChange = useCallback(
		(sortModel: GridSortModel) => {
			if (!sortModel.length) {
				return setSortOrder(defaultSortOrder);
			}

			const { field, sort } = sortModel[0];
			return setSortOrder({ field, sort });
		},
		[setSortOrder],
	);

	const handleEditRow = useCallback(
		(row) => {
			setEditingModel(row);
			setRows((prev) =>
				prev.map((user) => ({
					...user,
					isEditing: user.id === row.id,
				})),
			);
		},
		[setRows],
	);

	const handleResetEditing = useCallback(() => {
		setRows((prev) =>
			prev.map((user) => ({
				...user,
				isEditing: false,
			})),
		);
		setEditingModel(null);
	}, [setEditingModel, setRows]);

	const handleSaveRow = useCallback(
		(originalData) => {
			const handlers = {
				onCompleted: () => {
					handleResetEditing();
					setShowSnackbar({
						message: `Hourly account user ${editingModel.isNew ? 'created' : 'updated'} successfully`,
						severity: 'success',
					});
					refetch();
				},
				onError: (apolloError) => {
					setShowSnackbar({
						message: apolloError.message,
						severity: 'error',
					});
				},
			};

			// Handle the edge case where a user removes a validation
			// from the selected values and then adds the same one back.
			// We will be missing an ID when it is added back, which
			// will cause an error on the back-end.
			const validationsMap = originalData.hourlyAccountUserGroup.validationMethods.reduce((acc, item) => {
				acc[item.validationMethod.id] = item.id;
				return acc;
			}, {});

			const input = {
				userFirstName: editingModel.user?.firstName,
				userLastName: editingModel.user?.lastName,
				userEmail: editingModel.user?.email,
				accountId: hourlyAccount?.id,
				accountUserGroupInput: {
					name: `${editingModel.user.email}`,
					accountId: hourlyAccount?.id,
					masterPolicy: false,
					features:
						editingModel.hourlyAccountUserGroup?.userGroup?.functionGroups?.map(
							(functionGroups) => functionGroups.functionGroup.id,
						) ?? [],
					validations:
						editingModel.hourlyAccountUserGroup?.validationMethods?.map((validation) => ({
							id: validation.id ?? validationsMap[validation.validationMethod.id],
							validationMethodId: validation.validationMethod.id,
							webValidation: validation.webValidation,
							qrValidation: validation.qrValidation,
						})) ?? [],
				},
				organizationId: hourlyAccount?.organizationSettings?.organizationId,
				active: editingModel.active,
			};

			if (editingModel.isNew) {
				getUserExists({
					variables: {
						email: editingModel.user.email,
					},
					onCompleted: (response) => {
						const { getUserExists: exists } = response;

						if (exists) {
							createHourlyAccountUser({ variables: { input }, ...handlers });
						} else {
							const confirmationModal = (
								<SimpleModal
									content={`
                                    No account found for ${editingModel.user.email}.
                                    A new account will be created, do you want to continue?`}
									handleDismiss={() => setShowDialog(null)}
									handleConfirm={() => {
										setShowDialog(null);
										createHourlyAccountUser({ variables: { input }, ...handlers });
									}}
									dismissText='Cancel'
									confirmText='Confirm'
								/>
							);

							setShowDialog(confirmationModal);
						}
					},
				});
			} else {
				updateHourlyAccountUser({
					variables: {
						input: {
							...input,
							id: editingModel.id,
							userId: editingModel.user.userId,
							accountUserGroupInput: {
								...input.accountUserGroupInput,
								id: editingModel.hourlyAccountUserGroup.id,
							},
						},
					},
					...handlers,
				});
			}
		},
		[
			hourlyAccount,
			editingModel,
			refetch,
			setShowSnackbar,
			createHourlyAccountUser,
			updateHourlyAccountUser,
			handleResetEditing,
		],
	);

	const handleDeleteRow = useCallback(
		(row) => {
			const userName = `${row.user.firstName} ${row.user.lastName}`;
			const confirmationInputModal = (
				<ConfirmationInputModal
					title={`Are you sure you want to delete the user ${userName}`}
					validationString='DELETE'
					dismissText='Cancel'
					confirmText='Confirm'
					handleDismiss={() => setShowDialog(null)}
					handleConfirm={() =>
						deleteHourlyAccountUser({
							variables: {
								id: row.id,
							},
							onCompleted: () => {
								setShowDialog(null);
								setShowSnackbar({
									message: `Hourly account user deleted successfully`,
									severity: 'success',
								});
								refetch();
							},
							onError: (apolloError) => {
								setShowDialog(null);
								setShowSnackbar({
									message: apolloError.message,
									severity: 'error',
								});
							},
						})
					}
				/>
			);
			setShowDialog(confirmationInputModal);
		},
		[refetch, setShowSnackbar, setShowDialog, deleteHourlyAccountUser],
	);

	const handleAuditHistory = useCallback(
		(row) => {
			const dialog = (
				<AuditHistoryModal
					title='Hourly Portal User Audit History'
					resourceId={row.id}
					resourceType={ResourceTypes.HOURLY_ACCOUNT_USER}
					onClose={() => setShowDialog(null)}
				/>
			);

			setShowDialog(dialog);
		},
		[setShowDialog],
	);

	useEffect(() => {
		handleResetEditing();
	}, [handleResetEditing, disabled]);

	const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
		event.stopPropagation();
	};

	const columns: GridColDef<any>[] = [
		{
			field: 'actions',
			headerName: '',
			width: 150,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const isLoading = loading || createUserLoading || updateUserLoading;

				if (params.row.isEditing) {
					return (
						<>
							<IconButton onClick={() => handleSaveRow(params.row)} disabled={isLoading}>
								{createUserLoading || updateUserLoading ? (
									<CircularProgress size={18} sx={{ m: '3px 3px 2px 3px' }} />
								) : (
									<SaveIcon />
								)}
							</IconButton>
							<IconButton onClick={() => handleResetEditing()} disabled={isLoading}>
								<ClearIcon />
							</IconButton>
						</>
					);
				}

				return (
					<>
						<IconButton onClick={() => handleEditRow(params.row)} disabled={isLoading}>
							<EditIcon />
						</IconButton>
						<IconButton onClick={() => handleDeleteRow(params.row)} disabled={isLoading}>
							<DeleteIcon />
						</IconButton>
						<IconButton onClick={() => handleAuditHistory(params.row)} disabled={isLoading}>
							<HistoryIcon />
						</IconButton>
					</>
				);
			},
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			width: 150,
			renderCell: (params) => {
				if (params.row.isNew) {
					return (
						<TextField
							size='small'
							sx={{ width: '100%' }}
							value={editingModel.user.lastName}
							onKeyDown={handleInputKeyDown}
							onChange={(e) => {
								setEditingModel({
									...editingModel,
									user: {
										...editingModel.user,
										lastName: e.target.value,
									},
								});
							}}
						/>
					);
				}

				return <Box sx={{ pt: 1.25 }}>{params.row.user.lastName}</Box>;
			},
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			width: 150,
			renderCell: (params) => {
				if (params.row.isNew) {
					return (
						<TextField
							size='small'
							sx={{ width: '100%' }}
							value={editingModel.user.firstName}
							onKeyDown={handleInputKeyDown}
							onChange={(e) => {
								setEditingModel({
									...editingModel,
									user: {
										...editingModel.user,
										firstName: e.target.value,
									},
								});
							}}
						/>
					);
				}

				return <Box sx={{ pt: 1.25 }}>{params.row.user.firstName}</Box>;
			},
		},
		{
			field: 'email',
			headerName: 'Email Address',
			width: 250,
			renderCell: (params) => {
				if (params.row.isNew) {
					return (
						<TextField
							size='small'
							sx={{ width: '100%' }}
							value={editingModel.user.email}
							onKeyDown={handleInputKeyDown}
							onChange={(e) => {
								setEditingModel({
									...editingModel,
									user: {
										...editingModel.user,
										email: e.target.value,
									},
								});
							}}
						/>
					);
				}

				return <Box sx={{ pt: 1.25 }}>{params.row.user.email}</Box>;
			},
		},
		{
			field: 'active',
			type: 'boolean' as const,
			headerName: 'Active',
			width: 150,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				if (params.row.isEditing) {
					return (
						<Checkbox
							checked={editingModel.active}
							onChange={(e) => {
								setEditingModel({
									...editingModel,
									active: e.target.checked,
								});
							}}
						/>
					);
				}
				return params.row.active ? <CheckIcon /> : <CloseIcon />;
			},
		},
		{
			field: 'features',
			headerName: 'Features',
			flex: 1,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const base = params.row.isEditing ? editingModel : params.row;
				const { functionGroups } = base.hourlyAccountUserGroup.userGroup;
				const flatFunctionGroups = functionGroups.map(({ functionGroup }) => functionGroup);

				return (
					<PermissionFunctionGroupSelect
						editing={params.row.isEditing}
						value={flatFunctionGroups ?? []}
						options={features ?? []}
						onChange={(_, value) => {
							setEditingModel({
								...editingModel,
								hourlyAccountUserGroup: {
									...editingModel.hourlyAccountUserGroup,
									userGroup: {
										...editingModel.hourlyAccountUserGroup.userGroup,
										functionGroups: value.map((functionGroup) => ({
											functionGroup,
										})),
									},
								},
							});
						}}
					/>
				);
			},
		},
		{
			field: 'validations',
			headerName: 'Validation Methods',
			flex: 1,
			sortable: false,
			filterable: false,
			renderCell: (params) => {
				const base = params.row.isEditing ? editingModel : params.row;
				const { validationMethods } = base.hourlyAccountUserGroup;

				return (
					<ValidationMethodSelect
						editing={params.row.isEditing}
						value={validationMethods ?? []}
						options={validations ?? []}
						onChange={(_, value) => {
							setEditingModel({
								...editingModel,
								hourlyAccountUserGroup: {
									...editingModel.hourlyAccountUserGroup,
									validationMethods: value,
								},
							});
						}}
					/>
				);
			},
		},
	];

	return (
		<>
			<Box sx={{ display: 'flex' }}>
				<h3 style={{ marginTop: '10px', marginBottom: '10px' }}>Users</h3>
			</Box>
			<DataGridPro
				sx={{
					width: 'auto',
					height: 'auto',
					opacity: disabled ? 0.5 : 1,
					pointerEvents: disabled ? 'none' : 'unset',
				}}
				getRowHeight={() => 'auto'}
				rows={[...(editingModel?.isNew ? [editingModel] : []), ...rows]}
				rowCount={rowCount}
				columns={columns}
				loading={loading}
				pagination
				pageSizeOptions={[pageSize]}
				paginationMode='server'
				paginationModel={{ page, pageSize }}
				onPaginationModelChange={(pagination) => {
					setPaginationModel(pagination);
				}}
				sortingMode='server'
				onSortModelChange={onSortModelChange}
				filterMode='server'
				onFilterModelChange={onFilterModelChange}
				slots={{
					toolbar: GridToolbarSlot,
				}}
				slotProps={{
					toolbar: gridToolbarProps(PortalUsersToolbar, {
						editing: !!editingModel,
						onCreateAccount: () => {
							setEditingModel({
								isNew: true,
								isEditing: true,
								id: 0,
								active: true,
								user: {
									userId: null,
									lastName: '',
									firstName: '',
									email: '',
								},
								hourlyAccountUserGroup: {
									userGroup: {
										functionGroups: [],
									},
									validationMethods: [],
								},
							});
						},
					}),
				}}
			/>
		</>
	);
};
